import styles from "../css/Written.module.css";
import { useTranslation } from "react-i18next";
import AudioPlayer from "react-h5-audio-player";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import Term from "../../term/Term";
import SpeakButton from "../../common/SpeakButton";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import {
  EnglishPronunciation,
  getVoiceFile,
  makeBase64String,
} from "../../voice/util/voice";
import {
  getLearningAnswerSpeechFromLocalStroage,
  getMemoPositionFromLocalStorage,
  getLearningPronunciationPositionFromLocalStroage,
  getQuestionTypeFromLocalStorage,
  isOnLearningTextToSpeechFromLocalStorage,
  isOnWrongAnswerNoteFromLocalStorage,
} from "../../settings/util/learningSettings";
import { LearningType } from "../model/LearningType";
import { PronunciationPosition } from "../../settings/model/PronunciationPosition";
import { QuestionType } from "../../settings/model/QuestionType";
import PlaceHolderTextField from "../../common/PlaceHolderTextField";
import classNames from "classnames";
import {
  isHiraganaOnly,
  sanitizeAndSplitStringAndNormalize,
  sanitizeStringAndNormalize,
} from "../../common/strings";
import BigButton from "../../common/BigButton";
import EnterKeyListenerComponent from "../../common/keyListeners";
import { useReadingSettings } from "../../common/util/chineseCharacterMode";
import PronunciationSelectView from "../../common/view/PronunciationSelectView";
import MarkButton from "../../common/MarkButton";
import { formatShortcut } from "../../common/util/shourtcut";
import { MemoPosition } from "../../settings/model/MemoPosition";
import { LearningAnswerSpeech } from "../model/LearningAnswerSpeech";
import WrongAnswerNote from "./WrongAnswerNote";

interface WrittenProps {
  term: Term;
  englishPronunciation: EnglishPronunciation;
  showingPronunciationSelectView: boolean;
  onClickMarkButton: (term: Term) => void;
  onCorrect: (term: Term) => void;
  onIncorrect: (term: Term) => void;
  onSpeakError: (error: string) => void;
  onClickPronunciationSelectButton: (
    pronunciation: EnglishPronunciation
  ) => void;
}

export default function Written(props: WrittenProps) {
  const { t } = useTranslation();
  const { chineseCharacterMode } = useReadingSettings();

  const [audioSrc, setAudioSrc] = useState<string>();
  const [randomChoice] = useState<"term" | "definition">(
    Math.random() < 0.5 ? "term" : "definition"
  );
  const [currentQuestion, setCurrentQuestion] = useState<
    "term" | "definition" | "pron"
  >(getCurrentQuestionType());

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [userAnswer, setUserAnswer] = useState("");
  const [questionUpdateCount, setQuestionUpdateCount] = useState(0);
  const [currentGroup, setCurrentGroup] = useState("");
  const [currentGroupUpdateCount, setCurrentGroupUpdateCount] = useState(0);
  const [speakCount, setSpeakCount] = useState(0);
  const [enterKeyPressedCount, setEnterKeyPressedCount] = useState(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isResultMode, setIsResultMode] = useState(false);
  const [isAnswerResult, setIsAnswerResult] = useState(false);
  const [isShowingWrongAnswerNote, setIsShowingWrongAnswerNote] =
    useState(false);

  const speechShourtCut = formatShortcut({ mac: "⌘ S", win: "Ctrl + S" });

  // 같은 카드에서 단어를 편집하거나 별표를 눌러도 진행 상태가 초기화되지 않도록 id 가 바뀔 때만 새로 시작한다
  useEffect(() => {
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.term.id]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isSave = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s";

      if (!isSave) return;

      e.preventDefault();
      speak();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentGroupUpdateCount(currentGroupUpdateCount + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup]);

  useEffect(() => {
    setQuestionUpdateCount(questionUpdateCount + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (
        speakCount === 0 &&
        currentGroupUpdateCount > 0 &&
        questionUpdateCount > 0
      ) {
        if (!isOnLearningTextToSpeechFromLocalStorage()) return;
        if (currentQuestion === "definition") return;
        speak();
      }
    }, 50);

    return () => {
      clearTimeout(handler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, currentGroup]);

  useEffect(() => {
    let newQuestionString = "";
    let newAnswerString = "";
    const newQuestionGroupString = props.term.group;

    switch (currentQuestion) {
      case "term":
        newQuestionString = props.term.term;
        newAnswerString = props.term.definition;
        break;
      case "definition":
        newQuestionString = props.term.definition;
        newAnswerString = props.term.term;
        break;
      case "pron":
        newQuestionString = t("GuessTheDefinitionWithPronunciation");
        newAnswerString = props.term.definition;
        break;
    }

    setQuestion(newQuestionString);
    setAnswer(newAnswerString);
    setCurrentGroup(newQuestionGroupString);
  }, [currentQuestion, props.term, t]);

  function handleOnClickMarkButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.onClickMarkButton(props.term);
  }

  function handleOnClickSpeakButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    speak();
  }

  function handleOnPressEnterKey() {
    if (isShowingWrongAnswerNote) return;
    if (isResultMode === true) {
      if (enterKeyPressedCount > 0) {
        handleOnClickContinueButton();
      } else {
        setEnterKeyPressedCount(enterKeyPressedCount + 1);
      }
    }
  }

  function isShowPronunciation(): boolean {
    if (props.term.pronunciation.length === 0) return false;

    if (
      currentQuestion === "term" &&
      getLearningPronunciationPositionFromLocalStroage() ===
        PronunciationPosition.onTerm
    ) {
      return true;
    } else if (
      currentQuestion === "definition" &&
      getLearningPronunciationPositionFromLocalStroage() ===
        PronunciationPosition.onDefinition
    ) {
      return true;
    } else {
      return false;
    }
  }

  function reload() {
    setIsAnswerResult(false);
    setIsResultMode(false);
    setIsShowingWrongAnswerNote(false);
    setUserAnswer("");
    setSpeakCount(0);
    updateCurrentQuestionType();
  }

  function getCurrentQuestionType(): "term" | "definition" | "pron" {
    switch (getQuestionTypeFromLocalStorage(LearningType.spellingQuiz)) {
      case QuestionType.termFirst:
      case QuestionType.showAll:
        return "term";
      case QuestionType.definitionFirst:
        return "definition";
      case QuestionType.pronFirst:
        return "pron";
      case QuestionType.random:
        return randomChoice;
    }
  }

  function updateCurrentQuestionType() {
    switch (getQuestionTypeFromLocalStorage(LearningType.spellingQuiz)) {
      case QuestionType.termFirst:
        setCurrentQuestion("term");
        break;
      case QuestionType.definitionFirst:
        setCurrentQuestion("definition");
        break;
      case QuestionType.pronFirst:
        setCurrentQuestion("pron");
        break;
      case QuestionType.random:
        setCurrentQuestion(randomChoice);
        break;
    }
  }

  function isShowMemo(): boolean {
    const memoPositionValue = getMemoPositionFromLocalStorage(LearningType.spellingQuiz);
    if (isResultMode) return true;
    switch (memoPositionValue) {
      case MemoPosition.onTerm:
        return currentQuestion === "term" && props.term.memo.length !== 0;
      case MemoPosition.onDefinition:
        return currentQuestion === "definition" && props.term.memo.length !== 0;
      case MemoPosition.none:
        return false;
    }
  }

  function updateUserAnswer(answer: string) {
    setUserAnswer(answer);
  }

  function handleOnEnter() {
    if (userAnswer.length === 0) {
      setIsResultMode(true);
      setIsAnswerResult(false);
      return;
    }

    let userInputString = sanitizeAndSplitStringAndNormalize(userAnswer);
    let answers = sanitizeAndSplitStringAndNormalize(answer);
    let noSplitAnswer = sanitizeStringAndNormalize(answer);

    let isCorrect = false;

    if (sanitizeStringAndNormalize(userAnswer) === noSplitAnswer) {
      isCorrect = true;
    } else {
      const answerSet = new Set(answers);
      const allUserInputsAreCorrect = userInputString.every((input) =>
        answerSet.has(input)
      );

      if (allUserInputsAreCorrect) {
        isCorrect = true;
      }
    }

    const value = getLearningAnswerSpeechFromLocalStroage();
    if (isCorrect) {
      if (
        isOnLearningTextToSpeechFromLocalStorage() &&
        value === LearningAnswerSpeech.always
      ) {
        speak();
      }
    } else {
      if (
        isOnLearningTextToSpeechFromLocalStorage() &&
        (value === LearningAnswerSpeech.always ||
          value === LearningAnswerSpeech.onlyIncorrect)
      ) {
        speak();
      }
    }

    setIsResultMode(true);
    setIsAnswerResult(isCorrect);
  }

  function handleOnClickContinueButton() {
    setEnterKeyPressedCount(0);
    if (isAnswerResult) {
      props.onCorrect(props.term);
    } else if (isOnWrongAnswerNoteFromLocalStorage(LearningType.spellingQuiz)) {
      setIsShowingWrongAnswerNote(true);
    } else {
      props.onIncorrect(props.term);
    }
  }

  async function speak() {
    setSpeakCount(speakCount + 1);
    setIsPlaying(true);
    setAudioSrc(undefined);
    try {
      const languageCode =
        currentGroup.length !== 0
          ? await fetchGroupLanguageCode(currentGroup)
          : "";

      const text = isHiraganaOnly(props.term.pronunciation)
        ? props.term.pronunciation
        : props.term.term;

      const audio = await getVoiceFile(text, languageCode);
      setAudioSrc(makeBase64String(audio));
    } catch (error) {
      if (error instanceof Error) {
        props.onSpeakError(error.message);
      }
    }
  }

  const answerTitleTextClass = classNames(styles.yourAnswerTitle, {
    [styles.yourAnswerTitleCorrect]:
      isAnswerResult === true && isResultMode === true,
    [styles.yourAnswerTitleIncorrect]:
      isAnswerResult === false && isResultMode === true,
  });

  if (isShowingWrongAnswerNote) {
    return (
      <WrongAnswerNote
        term={props.term}
        userAnswer={userAnswer}
        englishPronunciation={props.englishPronunciation}
        showingPronunciationSelectView={props.showingPronunciationSelectView}
        onClickMarkButton={props.onClickMarkButton}
        onClickPronunciationSelectButton={
          props.onClickPronunciationSelectButton
        }
        onSpeakError={props.onSpeakError}
        onFinish={() => props.onIncorrect(props.term)}
      />
    );
  }

  return (
    <>
      <motion.div
        className={styles.containerForMotion}
        key={props.term.id}
        initial={{ x: 100, opacity: 0 }}
        animate={{
          rotateX: 0,
          x: 0,
          opacity: 1,
        }}
        transition={{
          duration: 0.3,
        }}
        style={{
          transformStyle: "preserve-3d",
        }}
      >
        <div className={styles.container}>
          <div className={styles.topContainer}>
            <div className={styles.topLeftContainer}>
              <MarkButton
                isMarked={props.term.isMarked}
                onClick={handleOnClickMarkButton}
              />
              <SpeakButton
                fontSize="small"
                isPlaying={isPlaying}
                onClick={handleOnClickSpeakButton}
                title={speechShourtCut}
              />
            </div>
            <div className={styles.topRightContainer}>
              <PronunciationSelectView
                open={props.showingPronunciationSelectView}
                selectedPronunciation={props.englishPronunciation}
                onClickAction={(
                  e: React.MouseEvent<HTMLDivElement>,
                  pronunciation: EnglishPronunciation
                ) => {
                  handleOnClickSpeakButton(e);
                  props.onClickPronunciationSelectButton(pronunciation);
                }}
              />
            </div>
          </div>
          <div
            className={styles.contentsContainer}
            lang={
              chineseCharacterMode === "japanese" ? "ja" : navigator?.language
            }
          >
            <div className={styles.questionContainer}>
              {isShowPronunciation() === true && (
                <p className={styles.pronunciationText}>
                  {`[${props.term.pronunciation}]`}
                </p>
              )}
              <p className={styles.questionText}>{question}</p>
              {isShowMemo() && (
                <p className={styles.memoText}>{props.term.memo}</p>
              )}
            </div>
            <div className={styles.answerContainer}>
              {isResultMode !== true && (
                <div className={styles.yourAnswerContainer}>
                  <p className={answerTitleTextClass}>Your answer</p>
                  <PlaceHolderTextField
                    hint={t("EnterCorrectAnswerText")}
                    lineStyle="single"
                    showingBorder={true}
                    autoFocus={true}
                    handleTextChange={updateUserAnswer}
                    onKeyDownInput={(e) => {
                      if (e.nativeEvent.isComposing) return;
                      if (e.key === "Enter") {
                        handleOnEnter();
                      }
                    }}
                  />
                </div>
              )}
              {isResultMode === true && (
                <div className={styles.answerResultContainer}>
                  {isAnswerResult === true && (
                    <div className={styles.wrongAnswerContainer}>
                      <p className={styles.correctAnswerText}>
                        {t("CorrectAnswer")}
                      </p>
                      <button className={styles.answerCorrectContainer}>
                        {answer}
                      </button>
                    </div>
                  )}
                  {isAnswerResult === false && (
                    <div className={styles.wrongAnswerContainer}>
                      <p className={styles.incorrectAnswerText}>
                        {t("IncorrectAnswerMessage")}
                      </p>
                      <button className={styles.answerIncorrectContainer}>
                        {userAnswer}
                      </button>
                      <p className={styles.correctAnswerText}>
                        {t("CorrectAnswer")}
                      </p>
                      <button className={styles.answerCorrectContainer}>
                        {answer}
                      </button>
                    </div>
                  )}
                  <BigButton
                    title={t("Continue")}
                    variant="normal"
                    handleClick={() => handleOnClickContinueButton()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {audioSrc && (
          <AudioPlayer
            key={audioSrc}
            autoPlay={true}
            src={audioSrc}
            style={{ display: "none" }}
            onEnded={() => {
              setIsPlaying(false);
              setAudioSrc(undefined);
            }}
          />
        )}
        <EnterKeyListenerComponent
          onEnterPress={() => handleOnPressEnterKey()}
        />
      </motion.div>
    </>
  );
}
